import { TFunction } from 'i18next'

import { Posting } from '@modules-deprecated/bankReconciliation/types'

import { Side } from '../../../enums/side'
import { Bill } from '../../bills/types/bill'
import { ExternalInvoiceFromBilly } from '../../invoices/types/externalInvoiceFromBilly'
import { Transaction } from '../../transactions/types/transaction'
import { BookkeepingItemType } from '../enums/bookkeepingItemType'
import { BookkeepingItem } from '../types/bookkeepingItem'

interface GetBookkeepingItemDescriptionParams {
  isInvoice: boolean
  isCreditNote: boolean
  bookkeepingItemNumber: string
  t: TFunction
  contactsMap: Map<string, string>
  contactId?: string | null
  contactName?: string | null
}

const getBookkeepingItemDescription = ({
  isInvoice,
  isCreditNote,
  bookkeepingItemNumber,
  t,
  contactsMap,
  contactId,
  contactName,
}: GetBookkeepingItemDescriptionParams): string => {
  const resolvedContactName = contactId ? contactsMap.get(contactId) : contactName
  let translationKey = ''

  if (isInvoice && !isCreditNote) {
    translationKey = 'bank_transactions.bookkeeping_item.invoice'
  } else if (isInvoice && isCreditNote) {
    translationKey = 'bank_transactions.bookkeeping_item.invoice_credit_note'
  } else if (!isInvoice && !isCreditNote) {
    translationKey = 'bank_transactions.bookkeeping_item.purchase'
  } else if (!isInvoice && isCreditNote) {
    translationKey = 'bank_transactions.bookkeeping_item.purchase_credit_note'
  }

  return t(translationKey, {
    number: bookkeepingItemNumber,
    contact: resolvedContactName || '',
  })
}

// Minimal invoice shape needed to build a bookkeeping item. Both the matching-list
// invoice (@modules-deprecated/app/invoices) and the fetched Billy invoice
// (@features/invoicesLegacy) satisfy this, so mapInvoice works for both.
interface MappableInvoice {
  id: string
  contactId: string
  invoiceNo: string
  balance: number
  entryDate: string
  currencyId: string
  exchangeRate?: number | null
  type: string | null
}

export const mapInvoice = (
  { id, contactId, invoiceNo, balance, entryDate, currencyId, exchangeRate, type }: MappableInvoice,
  contactsMap: Map<string, string>,
  t: TFunction,
): BookkeepingItem => {
  const isCreditNote = type === 'creditNote'
  const description = getBookkeepingItemDescription({
    isInvoice: true,
    isCreditNote,
    bookkeepingItemNumber: invoiceNo,
    t,
    contactsMap,
    contactId,
  })

  return {
    id,
    type: BookkeepingItemType.Invoice,
    amount: balance,
    date: entryDate,
    description,
    side: isCreditNote ? Side.Credit : Side.Debit,
    currency: currencyId,
    exchangeRate,
    isCreditNote,
  }
}

export const mapBill = (
  {
    id,
    contactId,
    contactName,
    voucherNo,
    balance,
    amount,
    entryDate,
    currencyId,
    exchangeRate,
    type,
    accountingBasis,
  }: Bill,
  contactsMap: Map<string, string>,
  t: TFunction,
): BookkeepingItem => {
  const isCreditNote = type === 'creditNote'
  const description = getBookkeepingItemDescription({
    isInvoice: false,
    isCreditNote,
    bookkeepingItemNumber: voucherNo || id,
    t,
    contactsMap,
    contactId,
    contactName,
  })

  return {
    id,
    type: BookkeepingItemType.Bill,
    amount: balance ?? amount ?? 0,
    date: entryDate || '',
    description,
    side: isCreditNote ? Side.Debit : Side.Credit,
    currency: currencyId || undefined,
    exchangeRate,
    isCreditNote,
    accountingBasis,
  }
}

export const mapExternalInvoice = (
  {
    id,
    contactId,
    number,
    externalId,
    balance,
    entryDate,
    currencyId,
    exchangeRate,
    type,
    accountingBasis,
  }: ExternalInvoiceFromBilly,
  contactsMap: Map<string, string>,
  t: TFunction,
): BookkeepingItem => {
  const isCreditNote = type === 'creditNote'

  const description = getBookkeepingItemDescription({
    isInvoice: true,
    isCreditNote,
    bookkeepingItemNumber: number,
    t,
    contactsMap,
    contactId,
  })

  return {
    id,
    externalId: externalId.toString(),
    type: BookkeepingItemType.ExternalInvoice,
    amount: balance,
    date: entryDate,
    description,
    side: isCreditNote ? Side.Credit : Side.Debit,
    currency: currencyId,
    exchangeRate,
    isCreditNote,
    accountingBasis,
  }
}

export const mapPosting = (
  { id, transactionId, amount, entryDate, text, side, currencyId }: Posting,
  t: TFunction,
  transaction?: Transaction,
): BookkeepingItem => {
  return {
    id,
    transactionId,
    type: BookkeepingItemType.Posting,
    amount,
    date: entryDate,
    description: transaction?.originatorName || text,
    side: side === 'credit' ? Side.Credit : Side.Debit,
    currency: currencyId,
  }
}
